import {
  LabelDisplayedRowsArgs,
  TablePagination,
  Theme,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import { arrayOf, element, number } from 'prop-types';
import { ElementType, FC, memo, useCallback, useMemo } from 'react';
import {
  ComponentPropType,
  PaginationActions,
  PaginationProps,
  sanitizeListRestProps,
  useListPaginationContext,
  useTranslate,
} from 'react-admin';

const MAX_RECORDS = 1000;

export type PaginationCustomProps = PaginationProps & {
  small?: boolean;
  handleFloatingButton?: boolean;
};

export const Pagination: FC<PaginationCustomProps> = memo((props) => {
  const {
    rowsPerPageOptions = DefaultRowsPerPageOptions,
    actions,
    small,
    handleFloatingButton,
    ...rest
  } = props;
  const {
    isLoading,
    hasNextPage,
    page,
    perPage,
    total: totalRaw,
    setPage,
    setPerPage,
  } = useListPaginationContext(props);
  const translate = useTranslate();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('md')) || small;

  const total = useMemo(() => (totalRaw > MAX_RECORDS ? MAX_RECORDS : totalRaw), [totalRaw]);

  const totalPages = useMemo(() => Math.ceil(total / perPage), [perPage, total]);

  /**
   * Warning: MUI's page is 0-based
   */
  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
      event && event.stopPropagation();
      if (page < 0 || page > totalPages - 1) {
        throw new Error(
          translate('ra.navigation.page_out_of_boundaries', {
            page: page + 1,
          }),
        );
      }
      setPage(page + 1);
    },
    [totalPages, setPage, translate],
  );

  const handlePerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setPerPage(+event.target.value);
    },
    [setPerPage],
  );

  const labelDisplayedRows = useCallback(
    ({ from, to, count }: LabelDisplayedRowsArgs) =>
      count === -1 && hasNextPage
        ? translate('ra.navigation.partial_page_range_info', {
            offsetBegin: from,
            offsetEnd: to,
            _: '%{from}-%{to} of more than %{to}',
          })
        : translate('ra.navigation.page_range_info', {
            offsetBegin: from,
            offsetEnd: to,
            total: `${count}${count === MAX_RECORDS ? '+' : ''}`,
            _: '%{from}-%{to} of %{count === -1 ? to : count}',
          }),
    [translate, hasNextPage],
  );

  const labelItem = useCallback(
    (type: string) => translate(`ra.navigation.${type}`, { _: `Go to ${type} page` }),
    [translate],
  );

  if (isLoading) {
    return <Toolbar variant="dense" />;
  }

  // Avoid rendering TablePagination if "page" value is invalid
  if (total === 0 || page < 1 || page > totalPages) {
    return null;
  }

  if (isSmall) {
    return (
      <TablePagination
        count={total}
        rowsPerPage={perPage}
        page={page - 1}
        onPageChange={handlePageChange}
        rowsPerPageOptions={emptyArray}
        component="span"
        labelDisplayedRows={labelDisplayedRows}
        {...sanitizeListRestProps(rest)}
      />
    );
  }

  const ActionsComponent = actions || PaginationActions;

  return (
    <TablePagination
      count={total}
      rowsPerPage={perPage}
      page={page - 1}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handlePerPageChange}
      ActionsComponent={ActionsComponent as unknown as ElementType<TablePaginationActionsProps>}
      slotProps={{ actions: { nextButton: { disabled: !hasNextPage } } }}
      component="span"
      labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
      labelDisplayedRows={labelDisplayedRows}
      getItemAriaLabel={labelItem}
      rowsPerPageOptions={rowsPerPageOptions}
      sx={handleFloatingButton ? { '& > div': { marginBottom: '50px' } } : {}}
      {...sanitizeListRestProps(rest)}
    />
  );
});

Pagination.propTypes = {
  actions: ComponentPropType,
  limit: element,
  rowsPerPageOptions: arrayOf(number) as React.Validator<number[] | null | undefined> | undefined,
};

const DefaultRowsPerPageOptions = [10, 15, 25, 50];
const emptyArray: number[] = [];
